import { useState } from 'react';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { apiPost } from '../../lib/apiClient.js';
import { exactWhen, relativeWhen } from '../../lib/date/relativeWhen.js';
import type { CustomerRelationshipAnalysisResponse } from '../../lib/types/customers.js';
import { useApiGet } from '../../lib/useApiGet.js';
import './customerCommunications.css';

const SENTIMENT_TREND_LABELS: Record<string, string> = {
  improving: 'روند احساس رو به بهبود',
  stable: 'روند احساس پایدار و رضایت‌بخش',
  deteriorating: 'روند احساس منفی و نیازمند مراقبت',
  neutral: 'روند احساس عادی و خنثی',
};

const RISK_LABELS: Record<string, string> = {
  low: 'ریسک کم',
  medium: 'ریسک متوسط',
  high: 'ریسک بالا',
  critical: 'ریسک بحرانی ریزش',
};

/** Persian names for the sections the analysis can read; keys match the API's section ids. */
const SECTION_LABELS: Record<string, string> = {
  deals: 'معاملات',
  quotes: 'پیش‌فاکتورها',
  orders: 'سفارش‌ها',
  receivables: 'دریافتنی‌ها',
  activities: 'فعالیت‌ها',
  notes: 'یادداشت‌ها',
  communications: 'تاریخچهٔ ارتباطات',
  callAnalyses: 'تحلیل تماس‌ها',
};

const fa = (n: number) => n.toLocaleString('fa-IR');

function sentimentTrendTone(trend: string | null): V2BadgeTone {
  if (trend === 'improving') return 'success';
  if (trend === 'deteriorating') return 'danger';
  if (trend === 'stable') return 'info';
  return 'neutral';
}

function riskTone(risk: string | null): V2BadgeTone {
  if (risk === 'critical' || risk === 'high') return 'danger';
  if (risk === 'medium') return 'warn';
  return 'success';
}

function healthTone(score: number): V2BadgeTone {
  if (score >= 70) return 'success';
  if (score >= 40) return 'warn';
  return 'danger';
}

function ListCard(props: { readonly title: string; readonly items: readonly string[] }) {
  if (props.items.length === 0) return null;
  return (
    <div className="c360-rel-analysis__card">
      <h4>{props.title}</h4>
      <ul>
        {props.items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * AI relationship analysis of one customer, built from every record the signed-in user may see
 * about them (the server assembles that dossier under the user's own permissions). Lives on the
 * Customer 360 overview; the communications workspace reuses it.
 *
 * Renders nothing when the user has no permission to read the analysis, so the overview never
 * shows an error panel for a capability the person was never given.
 */
export function CustomerRelationshipAnalysisCard(props: {
  readonly base: string;
  readonly partyRef: string;
}) {
  const encodedRef = encodeURIComponent(props.partyRef);
  const analysis = useApiGet<CustomerRelationshipAnalysisResponse>(
    props.base + '/api/v1/crm/communications/customers/' + encodedRef + '/relationship-analysis',
    [props.partyRef],
  );
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function trigger() {
    setAnalyzing(true);
    setError(null);
    try {
      await apiPost(
        props.base +
          '/api/v1/crm/communications/customers/' +
          encodedRef +
          '/relationship-analysis/trigger',
        {},
        { headers: { 'idempotency-key': `rel-ai-${props.partyRef}-${Date.now()}` } },
      );
      analysis.reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'به‌روزرسانی تحلیل مشتری انجام نشد.');
    } finally {
      setAnalyzing(false);
    }
  }

  if (analysis.status === 'error' && analysis.error.status === 403) return null;

  const data = analysis.status === 'ready' ? analysis.data.analysis : null;
  const coverage = data
    ? Object.entries(data.dataCoverage)
        .filter(([, count]) => count > 0)
        .map(([key, count]) => `${fa(count)} ${SECTION_LABELS[key] ?? key}`)
    : [];
  const unavailable = data ? data.unavailableSections.map((key) => SECTION_LABELS[key] ?? key) : [];

  return (
    <V2Card clipped>
      <section className="c360-rel-analysis" aria-label="تحلیل ساختار یار از مشتری">
        <div className="c360-rel-analysis__head">
          <div>
            <h3>تحلیل ساختار یار از مشتری</h3>
            <p>
              هوش مصنوعی همهٔ سوابق مجاز این مشتری را کنار هم می‌خواند: معاملات، پیش‌فاکتور و سفارش،
              دریافتنی‌ها، فعالیت‌ها، یادداشت‌ها، تماس‌ها و پیامک‌ها.
            </p>
          </div>
          <V2Button
            variant="secondary"
            size="sm"
            disabled={analyzing || analysis.status === 'loading'}
            onClick={() => void trigger()}
          >
            {analyzing ? 'در حال تحلیل…' : data ? 'به‌روزرسانی تحلیل' : 'تحلیل با هوش مصنوعی'}
          </V2Button>
        </div>

        <p className="c360-rel-analysis__cost">
          هر بار اجرا مقداری از اعتبار هوش مصنوعی سازمان را مصرف می‌کند و فقط با کلیک شما انجام
          می‌شود.
        </p>

        {error ? <p className="c360-comms-alert">{error}</p> : null}

        {analysis.status === 'loading' ? (
          <div className="c360-comms-loading">در حال بارگذاری تحلیل مشتری…</div>
        ) : data ? (
          <div className="c360-rel-analysis__body">
            {data.summary ? <p className="c360-rel-analysis__summary">{data.summary}</p> : null}

            <div className="c360-rel-analysis__chips">
              {data.healthScore !== null ? (
                <div className="c360-rel-analysis__chip">
                  <span>سلامت رابطه:</span>
                  <V2Badge tone={healthTone(data.healthScore)}>
                    {fa(data.healthScore)} از {fa(100)}
                  </V2Badge>
                </div>
              ) : null}
              <div className="c360-rel-analysis__chip">
                <span>روند احساس:</span>
                <V2Badge tone={sentimentTrendTone(data.sentimentTrend)}>
                  {SENTIMENT_TREND_LABELS[data.sentimentTrend ?? ''] ?? 'نامشخص'}
                </V2Badge>
              </div>
              <div className="c360-rel-analysis__chip">
                <span>ریسک ریزش:</span>
                <V2Badge tone={riskTone(data.churnRisk)}>
                  {RISK_LABELS[data.churnRisk ?? ''] ?? 'نامشخص'}
                </V2Badge>
              </div>
              {data.confidence !== null ? (
                <div className="c360-rel-analysis__chip">
                  <span>اطمینان تحلیل:</span>
                  <V2Badge tone="neutral">{fa(Math.round(data.confidence * 100))}٪</V2Badge>
                </div>
              ) : null}
            </div>

            <div className="c360-rel-analysis__grid">
              <ListCard title="نقاط قوت رابطه" items={data.strengths} />
              <ListCard title="دلایل ریسک ریزش" items={data.churnReasons} />
              <ListCard title="آخرین مسائل مطرح‌شده" items={data.latestIssues} />
              <ListCard title="تعهدات باز به مشتری" items={data.openCommitments} />
              <ListCard title="اقدامات پیگیری‌نشده" items={data.unhandledFollowups} />
              <ListCard title="فرصت‌های فروش" items={data.salesOpportunities} />
              <ListCard title="نکات مالی" items={data.financialInsights} />
            </div>

            {data.suggestedNextAction ? (
              <div className="c360-rel-analysis__next-action">
                <strong>پیشنهاد اقدام بعدی</strong>
                <p>{data.suggestedNextAction}</p>
              </div>
            ) : null}

            {data.healthScore === null ? (
              <p className="c360-rel-analysis__note">
                این تحلیل با روش قدیمی و فقط از چند ارتباط اخیر ساخته شده بود. برای تحلیل جامع همهٔ
                سوابق، «به‌روزرسانی تحلیل» را بزن.
              </p>
            ) : null}

            {coverage.length > 0 ? (
              <p className="c360-rel-analysis__note">مبنای تحلیل: {coverage.join('، ')}.</p>
            ) : null}
            {unavailable.length > 0 ? (
              <p className="c360-rel-analysis__note">
                این بخش‌ها به دلیل دسترسی شما در تحلیل دیده نشدند و نامعلوم حساب شدند:{' '}
                {unavailable.join('، ')}.
              </p>
            ) : null}

            <div className="c360-rel-analysis__footer">
              <small title={exactWhen(data.updatedAt)}>
                آخرین به‌روزرسانی تحلیل: {relativeWhen(data.updatedAt)}
              </small>
              <small>نتیجهٔ هوش مصنوعی است؛ پیش از تصمیم مهم با سوابق اصلی تطبیق بده.</small>
            </div>
          </div>
        ) : (
          <V2EmptyState
            icon="trend"
            title="تحلیل ساختار یار برای این مشتری هنوز ساخته نشده است"
            description="با «تحلیل با هوش مصنوعی» همهٔ سوابق مجاز مشتری یک‌جا بررسی می‌شود و وضعیت رابطه، ریسک و اقدام بعدی را می‌بینی."
            compact
          />
        )}
      </section>
    </V2Card>
  );
}
